/**
 * First run and guided setup (UX spec §3.1). A first run is when, after the first successful load of SETTINGS and
 * GOAL, there is no profile and no live goal; the queries have no persisted cache, so success means a full pull came
 * back and an existing user on a new browser is never sent to setup.
 */
import { readUnits } from '../sync/settings'
import type { Units } from './units'

/** Set by "Finish setup later": stops the automatic redirect only; the dashboard banner still shows. */
export const SETUP_LATER_KEY = 'macrotide.setupLater'

/** Setup steps by index; the Done screen follows the last. */
export const SETUP_STEPS = ['Your goal', 'About you', 'Starting point', 'Nutrition program'] as const
export const SETUP_DONE_STEP = SETUP_STEPS.length

export interface SetupState {
  hasGoal: boolean
  hasProfile: boolean
  /** SETTINGS `algorithms` saved: the starting-point answers are optional, so any saved group counts. */
  hasAlgorithms: boolean
  hasProgram: boolean
}

/** Whether to send the user to setup now: everything loaded, nothing set up, and not put off. */
export function needsFirstRun(loaded: boolean, state: Pick<SetupState, 'hasGoal' | 'hasProfile'>, setupLater: boolean): boolean {
  return loaded && !state.hasGoal && !state.hasProfile && !setupLater
}

/** The Finish setup banner shows while the goal or the profile is missing, whatever the flag says. */
export function setupIncomplete(state: Pick<SetupState, 'hasGoal' | 'hasProfile'>): boolean {
  return !state.hasGoal || !state.hasProfile
}

/**
 * Where "Continue" resumes, decided from the data and not a stored step: no goal → 0, no profile → 1, no nutrition
 * program → 3. Step 2's answers are optional, so it never holds the user back.
 */
export function firstIncompleteStep(state: SetupState): number {
  if (!state.hasGoal) return 0
  if (!state.hasProfile) return 1
  if (!state.hasProgram) return 3
  return SETUP_DONE_STEP
}

/** How many of the four steps are done, for "1 of 4 done". */
export function stepsDone(state: SetupState): number {
  return [state.hasGoal, state.hasProfile, state.hasAlgorithms, state.hasProgram].filter(Boolean).length
}

/**
 * The step labels [SetupPage] shows, [trainingApplies] (`trainingStepApplies` in `./setup`) adding the extra,
 * skippable "Your training" step between the nutrition program and Done. Someone who doesn't train never sees it:
 * the step count itself changes, not just whether it is reachable.
 */
export function setupStepLabels(trainingApplies: boolean): readonly string[] {
  return trainingApplies ? [...SETUP_STEPS, 'Your training'] : SETUP_STEPS
}

/** Where Done falls for this user: one past the nutrition program, or one further still with the training step. */
export function setupDoneStep(trainingApplies: boolean): number {
  return setupStepLabels(trainingApplies).length
}

/**
 * One more than [SETUP_DONE_STEP]: someone who trains (`trainingStepApplies` in `./setup`) gets an extra, skippable
 * "Your training" step between the nutrition program and Done, so the URL needs to reach one step further.
 */
export const MAX_SETUP_STEP = SETUP_DONE_STEP + 1

/** The step in a `/setup/$step` URL, or null when it is not one (the route then redirects to step 0). */
export function parseSetupStep(value: string): number | null {
  if (!/^\d$/.test(value)) return null
  const step = Number(value)
  return step <= MAX_SETUP_STEP ? step : null
}

export function readSetupLater(): boolean {
  try {
    return window.localStorage.getItem(SETUP_LATER_KEY) === 'true'
  } catch {
    return false
  }
}

export function writeSetupLater(): void {
  try {
    window.localStorage.setItem(SETUP_LATER_KEY, 'true')
  } catch {
    // Blocked storage: setup is offered again next time, which is the safe side.
  }
}

export const SETUP_UNITS_KEY = 'macrotide.setupUnits'

/** Units picked in setup before a profile exists to hold them; null when none were picked here. */
export function readSetupUnits(): Units | null {
  try {
    const text = window.localStorage.getItem(SETUP_UNITS_KEY)
    return text === null ? null : readUnits(JSON.parse(text))
  } catch {
    return null
  }
}

export function writeSetupUnits(units: Units): void {
  try {
    window.localStorage.setItem(SETUP_UNITS_KEY, JSON.stringify(units))
  } catch {
    // Blocked storage: the choice still holds for this visit, and the profile keeps it once saved.
  }
}
